import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import styles from '../styles/components/Preferences.module.scss';
import BodyFrontImg from '../assets/images/body-front.svg';
import BodyBackImg from '../assets/images/body-back.svg';
import { Dropdown } from './core/Dropdown';
import { useToast } from './core/ToastManager';

export type PreferenceOption = 'ok' | 'avoid' | 'focus';

interface PreferencesFormData {
  preferences: Record<string, PreferenceOption>;
  notes: string;
}

interface Props {
  onClose: () => void;
  setUserPreferences: (notes: string) => void;
  selectedPreferences: Record<string, PreferenceOption> | null;
  setSelectedPreferences: (preferences: Record<string, PreferenceOption>) => void;
  notes: string;
  setNotes: (notes: string) => void;
}

const options = [
  { value: 'ok', label: 'Ok' },
  { value: 'avoid', label: 'Avoid' },
  { value: 'focus', label: 'Focus' },
];

const frontAreas = [
  { name: 'Head', key: 'head' },
  { name: 'Face', key: 'face' },
  { name: 'Pecs', key: 'pecs' },
  { name: 'Arms & Hands', key: 'armsHands' },
  { name: 'Quads', key: 'quads' },
  { name: 'Feet', key: 'feet' },
];

const backAreas = [
  { name: 'Neck', key: 'neck' },
  { name: 'Shoulders', key: 'shoulders' },
  { name: 'Upper Back', key: 'upperBack' },
  { name: 'Mid Back', key: 'midBack' },
  { name: 'Lower Back', key: 'lowerBack' },
  { name: 'Hips & Glutes', key: 'hipsGlutes' },
  { name: 'Hamstrings', key: 'hamstrings' },
  { name: 'Calves', key: 'calves' },
];

export const PreferencesModal: React.FC<Props> = ({
  onClose,
  setUserPreferences,
  selectedPreferences,
  setSelectedPreferences,
  notes,
  setNotes
}) => {

  // The maximum allowed 'focus' selections is set to 2, as per the requirements
  const maxFocusPreferenceCount = 2
  const [focusCount, setFocusCount] = useState(0);
  
  const { control, handleSubmit, watch } = useForm<PreferencesFormData>({
    defaultValues: {
      preferences: selectedPreferences || {
        head: 'ok',
        face: 'ok',
        pecs: 'ok',
        armsHands: 'ok',
        quads: 'ok',
        feet: 'ok',
        neck: 'ok',
        shoulders: 'ok',
        upperBack: 'ok',
        midBack: 'ok',
        lowerBack: 'ok',
        hipsGlutes: 'ok',
        hamstrings: 'ok',
        calves: 'ok',
      },
      notes: notes,
    }
  });

  const { addToast } = useToast();
  const preferences = watch('preferences');

  const onSubmit = (data: PreferencesFormData) => {
    setSelectedPreferences(data.preferences);
    setNotes(data.notes);
  
    // Group preferences by category
    const groupedPreferences: Record<string, string[]> = { focus: [], avoid: [] };
  
    Object.entries(data.preferences).forEach(([key, value]) => {
      if (value === 'avoid' || value === 'focus') {
        groupedPreferences[value].push(formatArea(key));
      }
    });
  
    const formattedPreferences = Object.entries(groupedPreferences)
      .filter(([_, areas]) => areas.length > 0) // Only include non-empty categories
      .map(([category, areas]) => {
        const formattedCategory = category.charAt(0).toUpperCase() + category.slice(1); // Capitalize "Focus"/"Avoid"
        return `${formattedCategory}:\n${areas.join('\n')}`;
      })
      .join('\n\n');
  
    const formattedString = `${formattedPreferences}\n\nNotes: "${data.notes}"`;

    addToast('Preferences updated successfully', 'success');
    setUserPreferences(formattedString);
    onClose();
  };
  
  const formatArea = (key: string): string => {
    const areaMap: Record<string, string> = {
      head: 'Front - Head',
      face: 'Front - Face',
      pecs: 'Front - Chest (Pecs)',
      armsHands: 'Front - Arms & Hands',
      quads: 'Front - Thighs (Quads)',
      feet: 'Front - Feet',
      neck: 'Back - Neck',
      shoulders: 'Back - Shoulders',
      upperBack: 'Back - Upper Back',
      midBack: 'Back - Mid Back',
      lowerBack: 'Back - Lower Back',
      hipsGlutes: 'Back - Hips & Glutes',
      hamstrings: 'Back - Hamstrings',
      calves: 'Back - Calves',
    };
  
    return areaMap[key] || key;
  };
  

  const getCircleColor = (value: PreferenceOption) => {
    switch (value) {
      case 'avoid':
        return '#acacac'; 
      case 'focus':
        return '#404d6a';
      default:
        return '#fefcf5';
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.preferences}>
      <h2>Are there any areas that you would like your therapist to focus on or avoid?</h2>
      <p>Select all areas that apply. Up to 2 focus areas can be selected.</p>

      <div className={styles.heading}>
        <h2>Front</h2>
      </div>
      <div className={styles.preference}>
        <div className={styles.preferenceForm}>
          {frontAreas.map((area, index) => {
            return (
              <div key={area.key + index} className={styles.preferenceRow}>
                <label>{area.name}</label>
                <Controller
                  name={`preferences.${area.key}` as const}
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      id={area.key + index}
                      label={''}
                      value={field.value as string}
                      options={options}
                      onChange={(selectedValue)=> {
                        field.onChange(selectedValue)
                        // Detect when the user selects an option other than 'focus'
                        if (selectedValue !== 'focus' && field.value === 'focus') {
                          setFocusCount(prevCount => prevCount - 1)
                        }
                        // Detect when the user selects 'focus' as an option
                        else if(selectedValue === 'focus' && field.value !== 'focus') {
                          setFocusCount(prevCount => prevCount + 1)
                        }
                      }}
                      disabledOptionKey={focusCount >= maxFocusPreferenceCount && field.value !== 'focus' ? 'focus' : undefined}
                      className={styles.dropdown}
                    />
                  )}
                />
              </div>
            );
          })}
        </div>
        <div className={styles.preferenceLayout}>
          <img src={BodyFrontImg} alt="Body front" />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.head), top: '0', left: '55%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.face), top: '2%', left: '25%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.pecs), top: '17%', left: '25%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.pecs), top: '17%', right: '23%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.armsHands), top: '33%', right: '1%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.armsHands), top: '33%', left: '0%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.armsHands), top: '53%', right: '1%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.armsHands), top: '53%', left: '0%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.quads), top: '60%', right: '27%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.quads), top: '60%', left: '26%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.feet), top: '94%', right: '20%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.feet), top: '94%', left: '18%' }}
          />
        </div>
      </div>

      <div className={styles.heading}>
        <h2>Back</h2>
      </div>
      <div className={styles.preference}>
        <div className={styles.preferenceForm}>
          {backAreas.map((area, index) => {
            return (
              <div key={area.key + index}>
                <div className={styles.preferenceRow}>
                  <label>{area.name}</label>
                  <Controller
                    name={`preferences.${area.key}` as const}
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        id={area.key + index}
                        label={''}
                        value={field.value as string}
                        options={options}
                        onChange={(selectedValue)=> {
                          field.onChange(selectedValue)
                          // Detect when the user selects an option other than 'focus'
                          if (selectedValue !== 'focus' && field.value === 'focus') {
                            setFocusCount(prevCount => prevCount - 1)
                          }
                          // Detect when the user selects 'focus' as an option
                          else if(selectedValue === 'focus' && field.value !== 'focus') {
                            setFocusCount(prevCount => prevCount + 1)
                          }
                        }}
                        disabledOptionKey={focusCount >= maxFocusPreferenceCount && field.value !== 'focus' ? 'focus' : undefined}
                        className={styles.dropdown}
                      />
                    )}
                  />
                </div>
                {/* uncomment if need to return the special note */}
                {/* {area.key === 'hipsGlutes' && (
                    <p className={styles.note}>
                      By default, gluteal work is performed on top of the sheet
                    </p>
                  )} */}
              </div>
            );
          })}
        </div>
        <div className={styles.preferenceLayout}>
          <img src={BodyBackImg} alt="Body back" />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.neck), top: '10%', left: '40%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.shoulders), top: '15%', left: '13%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.shoulders), top: '15%', right: '13%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.upperBack), top: '20%', left: '40%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.midBack), top: '28%', left: '40%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.lowerBack), top: '37%', left: '40%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.hipsGlutes), top: '45%', left: '19%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.hipsGlutes), top: '45%', right: '19%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.hamstrings), top: '63%', left: '29%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.hamstrings), top: '63%', right: '27%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.calves), top: '74%', left: '24%' }}
          />
          <div
            className={styles.circle}
            style={{ backgroundColor: getCircleColor(preferences.calves), top: '74%', right: '20%' }}
          />
        </div>
      </div>

      <div className={styles.messageForm}>
        <h2>Is there anything else you would like our team to know prior to your service?</h2>
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <textarea {...field} placeholder="Type your message here" maxLength={500} />
          )}
        />
      </div>

      <div className={styles.actions}>
        <button type="submit" className="button">
          Save Preferences
        </button>
        <button type="button" className="button__underline" onClick={() => onClose()}>
          CANCEL
        </button>
      </div>
    </form>
  );
};

export default PreferencesModal;